import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { useTonAddress, useTonConnectUI } from '@tonconnect/ui-react';
import { useNavigate } from 'react-router-dom';

import {
  headerStyles,
  backgroundAnimationStyles,
  contentStyles,
  tonTextStyles,
  valueTextStyles,
  usdTextStyles,
  buttonContainerStyles,
  buttonTextStyles,
  connectButtonWrapperStyles,
  connectedWalletContainerStyles,
  connectedWalletWrapperStyles,
  walletAddressContainerStyles,
  addressTextStyles,
  dividerStyles
} from './styles';

import LottieAnimation from '@/animations/LottieAnimation.tsx';
import starsAnimation from '@/components/common/svg/assets/stars_background.json';
import LogoutShape from '@/components/common/svg/shapes/LogoutShape.tsx';
import { useDisconnectWallet } from '@/hooks/useDisconnectWallet.tsx';
import { useTonStore } from '@/store/useTonStore.ts';
import { helpers } from '@/helpers.ts';
import BalanceSkeleton from '@/components/EarnTon/Header/BalanceSkeleton.tsx';
import { routes } from '@/navigation/routes.tsx';

const Header: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [tonConnectUI] = useTonConnectUI();
  const userFriendlyAddress = useTonAddress();

  const { balance, fetchBalance } = useTonStore();

  useEffect(() => {
    const getBalance = async () => {
      if (!balance) await fetchBalance();
    };

    void getBalance();
  }, []);

  const openConnectionModal = async () => {
    await tonConnectUI.openModal();
  };

  const connectWallet = async () => {
    await openConnectionModal();
  };

  const disconnectWallet = async () => {
    await tonConnectUI.disconnect();
  };

  const disconnectPopup = useDisconnectWallet(disconnectWallet);

  const formatAddress = (address: string) => {
    if (!address) return '';
    return `${address.slice(0, 5)}...${address.slice(-5)}`;
  };

  return (
    <div style={headerStyles}>
      <div style={backgroundAnimationStyles}>
        <LottieAnimation
          width={300}
          height={300}
          speed={0.5}
          animationData={starsAnimation}
          shouldAnimate={true}
          loop={true}
        />
      </div>

      <div style={{ position: 'relative' }}>
        {!balance ? (
          <BalanceSkeleton />
        ) : (
          <div style={contentStyles}>
            <p style={tonTextStyles}>{t('earn.withdraw.available_balance')}</p>
            <p style={valueTextStyles}>
              {helpers.convertMicrosToTON(balance?.ton_micros)}&nbsp;
              <span style={{ fontWeight: 'normal', fontSize: '20px' }}>
                TON
              </span>
            </p>
            <p style={usdTextStyles}>
              ≈ {helpers.convertCentsToUSD(balance?.usd_cents)} USD
            </p>
          </div>
        )}

        {!userFriendlyAddress && (
          <motion.div style={connectButtonWrapperStyles}>
            <span style={buttonTextStyles} onClick={connectWallet}>
              {t('earn.connect_wallet')}
            </span>
          </motion.div>
        )}
      </div>

      {userFriendlyAddress && (
        <div style={connectedWalletContainerStyles}>
          <div style={connectedWalletWrapperStyles}>
            <div style={walletAddressContainerStyles}>
              <div
                style={{ display: 'flex', alignItems: 'center', gap: '10px' }}
              >
                <span style={addressTextStyles}>
                  {formatAddress(userFriendlyAddress)}
                </span>
                <div
                  style={{ display: 'flex', alignItems: 'center' }}
                  onClick={disconnectPopup}
                >
                  <LogoutShape height={20} width={20} />
                </div>
              </div>
              <motion.div style={buttonContainerStyles}>
                <span
                  style={buttonTextStyles}
                  onClick={() => navigate(routes.withdrawTon)}
                >
                  {t('earn.withdraw')}
                </span>
              </motion.div>
            </div>
            <div style={dividerStyles} />
          </div>
        </div>
      )}
    </div>
  );
};

export default Header;
